import { useState } from 'react'
import { Button, Separator, useFeedback } from '@/components/ui'
import { BookingWidget } from './BookingWidget'
import { cn } from '@/lib/cn'
import { trpc } from '@/lib/trpc'
import { trackFormSubmit } from '@/lib/analytics'

interface ContactFormProps {
  /** Show the booking scheduler beneath the message form. */
  showBooking?: boolean
  bookingHeading?: string
  header: string
  body: string
  className?: string
}

export function ContactForm({
  showBooking = false,
  bookingHeading = 'Or book a free call',
  header,
  body,
  className,
}: ContactFormProps) {
  const { toast } = useFeedback()
  const [form, setForm] = useState({ name: '', email: '', message: '', honeypot: '' })
  const [submitted, setSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Honeypot check
    if (form.honeypot) {
      return
    }

    setIsSubmitting(true)

    try {
      const [result] = await Promise.all([
        trpc.email.send.mutate({
          to: ['garrett@garrettjohnlaw.com'],
          subject: `New message from ${form.name}`,
          text: `From: ${form.email}\n\n${form.message}`,
        }),
        // Add to CRM as inbound lead (non-blocking, don't await failure)
        trpc.leads.create
          .mutate({
            name: form.name,
            email: form.email,
            message: form.message,
            source: 'Contact Form',
          })
          .catch(() => {}),
      ])

      if (result.success) {
        trackFormSubmit('contact', form.email)
        setSubmitted(true)
      } else {
        toast('Failed to send message. Please try again.', 'error')
      }
    } catch {
      toast('Failed to send message. Please try again.', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={cn('bg-transparent py-24', className)}>
      <div className="mx-auto max-w-xl space-y-8 px-4">
        <section className="space-y-2 text-center">
          <p className="text-4xl font-bold tracking-tight">{header}</p>
          <Separator className="bg-border mx-auto h-[2px] w-16" />
          <p>{body}</p>
        </section>

        {submitted ? (
          <div className="text-center text-lg font-medium text-green-600">Message sent successfully!</div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="mb-1 block text-sm font-medium">
                Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={form.name}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                className="border-border bg-background text-foreground focus:ring-primary w-full rounded-md border px-3 py-2 focus:ring-2 focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="email" className="mb-1 block text-sm font-medium">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                className="border-border bg-background text-foreground focus:ring-primary w-full rounded-md border px-3 py-2 focus:ring-2 focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="message" className="mb-1 block text-sm font-medium">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                value={form.message}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                className="border-border bg-background text-foreground focus:ring-primary w-full resize-none rounded-md border px-3 py-2 focus:ring-2 focus:outline-none"
              />
            </div>
            {/* Honeypot field */}
            <input
              type="text"
              name="honeypot"
              value={form.honeypot}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="pointer-events-none absolute left-[-9999px] h-0 w-0 opacity-0"
            />
            <Button type="submit" variant="default" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </Button>
          </form>
        )}

        {showBooking && (
          <div className="pt-12">
            <h2 className="mb-4 text-center text-xl font-semibold">{bookingHeading}</h2>
            <BookingWidget
              prefill={{ name: form.name, email: form.email }}
              metadata={{ source_page: 'contact-form' }}
              analyticsSource="contact-form"
            />
          </div>
        )}
      </div>
    </div>
  )
}
